// 수신 메시지 조회·분류 API — /api/v2/inbound-messages/*
// (전역 관리자 + 라인업 owner 를 열려고 /admin 우산 밖으로 뺐다 — 백엔드 requireInboundMessageAccess)
import { authFetch, authForm, authJson } from "./_client";

const API = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/inbound-messages`;

export type InboundMessage = {
  id: number;
  source: string; // 앱 키: sms | kakao | telegram | <패키지명>(그 외 모든 앱)
  sender: string;
  body: string;
  deviceId: string;
  smsDirection: "inbound" | "outbound" | null;
  category: string | null;
  isRead: boolean;
  starred: boolean;
  receivedAt: string;
  canReply?: boolean;
  // 발신번호가 우리 고객이면 서버가 붙여 준다(알림 조회에서만). 모르는 번호면 null.
  customerId?: number | null;
  customerName?: string | null;
};

export type InboundList = {
  items: InboundMessage[];
  hasMore: boolean;
  page: number;
  pageSize: number;
};

export type InboundFilters = {
  source?: string; // 앱 키 — 특정 앱만(sms/kakao/telegram/패키지명)
  device?: string; // 폰(기기) device_id — 특정 폰만
  sender?: string; // 발신자 — 특정 발신자만(정확 일치)
  category?: string; // 코드값 | "none"(미분류)
  q?: string;
  read?: "unread";
  starred?: "1";
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export type CaptureDiagnostics = {
  schemaVersion: 1;
  reportedAt: string;
  manufacturer: string;
  model: string;
  androidVersion: string;
  sdkInt: number;
  receiveSmsGranted: boolean;
  readSmsGranted: boolean;
  notificationAccessGranted: boolean;
  notificationListenerConnected: boolean;
  alwaysOn: boolean;
  serviceRunning: boolean;
  batteryExempt: boolean;
  lastSmsBroadcastAt: string | null;
  lastSmsQueuedAt: string | null;
  lastMessagingNotificationAt: string | null;
  lastScanStartedAt: string | null;
  lastScanSucceededAt: string | null;
  lastUploadSucceededAt: string | null;
  lastScanError: string | null;
  lastUploadError: string | null;
};

// 폰(기기) — 수신 포워더가 깔린 폰 하나. label=사람이 지은 이름(없으면 device_id 로 표시).
export type InboundDevice = {
  deviceId: string;
  label: string | null;
  phoneNumber: string | null;
  /** APK가 실제 기본 문자 회선에서 읽어 보고한 번호. */
  reportedPhoneNumber: string | null;
  /** 폰이 번호를 못 알려줄 때 대표가 직접 확인한 당시의 등록 번호. */
  manuallyVerifiedPhoneNumber: string | null;
  lastSeenAt: string;
  senderDeviceId: string | null;
  senderName: string | null;
  senderDisconnectedByReplacement: boolean;
  senderOnline: boolean;
  senderAppVersion: string | null;
  captureDiagnostics: CaptureDiagnostics | null;
};

// 앱 — 알림을 보내는 앱 하나. appKey=sms/kakao/telegram/패키지명. label=사람이 읽는 이름.
// ignored=true 면 수집 안 함(수신 단계에서 버림)+기본 목록에서 숨김.
export type InboundApp = {
  appKey: string;
  label: string | null;
  ignored: boolean;
  lastSeenAt: string | null;
};

export function listInboundMessages(f: InboundFilters): Promise<InboundList> {
  const qs = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== "" && v !== null) qs.set(k, String(v));
  });
  return authJson<InboundList>(`${API}?${qs.toString()}`);
}

export function patchInboundMessage(
  id: number,
  patch: { category?: string | null; isRead?: boolean; starred?: boolean },
): Promise<InboundMessage> {
  return authJson<InboundMessage>(`${API}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function classifyInbound(limit = 30): Promise<{ classified: number }> {
  return authJson<{ classified: number }>(`${API}/classify`, {
    method: "POST",
    body: JSON.stringify({ limit }),
  });
}

// 폰 목록 — 폰별 필터 드롭다운 + 이름 관리에 쓴다.
export function listInboundDevices(): Promise<{ devices: InboundDevice[] }> {
  return authJson<{ devices: InboundDevice[] }>(`${API}/devices`);
}

// 폰 이름 변경 — label 을 빈 문자열/null 로 주면 이름 지움(device_id 로 폴백).
export function renameInboundDevice(
  deviceId: string,
  label: string | null,
  phoneNumber?: string | null,
): Promise<InboundDevice> {
  return authJson<InboundDevice>(`${API}/devices/${encodeURIComponent(deviceId)}`, {
    method: "PATCH",
    body: JSON.stringify({ label, ...(phoneNumber !== undefined ? { phoneNumber } : {}) }),
  });
}

export function setInboundDevicePhoneVerification(
  deviceId: string,
  verified: boolean,
): Promise<InboundDevice> {
  return authJson<InboundDevice>(
    `${API}/devices/${encodeURIComponent(deviceId)}/phone-line-verification`,
    {
      method: "PATCH",
      body: JSON.stringify({ verified }),
    },
  );
}

/**
 * 알림 카드 한 장이 쓰는 값 — **문자 전체가 아니라 딱 이만큼만** 서버가 내려준다(티켓 #507).
 *
 * 종전에는 문자 표 18칸을 통째로 받아 6칸만 썼다. 이 조회는 매장 PC 마다 주기적으로 도는
 * 상시 비용이라, 여기에 칸을 늘릴 땐 카드가 정말 그 값을 읽는지부터 확인한다.
 */
export type SmsAlertMessage = Pick<
  InboundMessage,
  | "id"
  | "source"
  | "sender"
  | "body"
  | "deviceId"
  | "receivedAt"
  | "canReply"
  | "customerId"
  | "customerName"
> & {
  alertId: number;
  /** 이 문자를 받은 폰에 사람이 등록한 번호. */
  receivingPhoneNumber: string | null;
  /** 번호가 없을 때 보여 줄 폰 이름 → 기기명 → 매장폰 폴백. */
  receivingDeviceName: string;
  /** 답장을 실제로 발신할 기본 문자 회선 번호. */
  replyPhoneNumber: string | null;
  /** 폰이 번호를 못 알려줄 때 대표가 직접 확인한 번호. */
  manuallyVerifiedPhoneNumber: string | null;
  /** 이 문자를 받은 수신폰과 연결된 기본 발송기. 카드의 최초 선택에만 쓴다. */
  replyDeviceId: string;
};

type SmsAlertPage = {
  items: SmsAlertMessage[];
  hasMore: boolean;
  nextAfterAlertId: number;
};

/** 아직 아무도 처리하지 않은 문자 알림을 페이지 끝까지 가져온다. */
export async function fetchPendingSmsAlerts(): Promise<{ items: SmsAlertMessage[] }> {
  const items: SmsAlertMessage[] = [];
  let afterAlertId = 0;

  for (let pageNo = 0; pageNo < 50; pageNo += 1) {
    const page = await authJson<SmsAlertPage>(
      `${API}/alerts/pending?afterAlertId=${afterAlertId}&limit=200`,
    );
    items.push(...page.items);
    if (!page.hasMore || page.nextAfterAlertId <= afterAlertId) break;
    afterAlertId = page.nextAfterAlertId;
  }

  return { items };
}

/**
 * 이 알림들을 "사람이 처리했다"로 서버에 남긴다 — 답장을 보냈거나 ✕로 닫았을 때만 부른다(티켓 #431).
 * 아이디 단위로 공유돼 같은 아이디로 켜 둔 다른 PC의 카드도 다음 조회 때 사라진다.
 * (화면에 띄우기만 한 알림은 서버에 알리지 않는다 — 그래야 다른 PC에도 같은 카드가 뜬다.)
 */
export function dismissSmsAlerts(alertIds: number[]): Promise<{ ok: true; alertIds: number[] }> {
  return authJson<{ ok: true; alertIds: number[] }>(`${API}/alerts/dismiss`, {
    method: "POST",
    body: JSON.stringify({ alertIds }),
  });
}

/** 알림을 꺼 둔 번호 하나 — 목록 화면(다시 켜기)에서 쓴다. */
export type SmsAlertMute = {
  phone: string;
  createdAt: string;
  customerId: number | null;
  customerName: string | null;
};

export function fetchSmsAlertMutes(): Promise<{ items: SmsAlertMute[] }> {
  return authJson<{ items: SmsAlertMute[] }>(`${API}/alerts/mutes`);
}

/** 이 번호 알림 끄기 — 문자는 계속 받되 팝업·데스크톱 알림만 멈춘다. */
export function muteSmsAlerts(phone: string): Promise<{ ok: true; phone: string }> {
  return authJson<{ ok: true; phone: string }>(`${API}/alerts/mutes`, {
    method: "POST",
    body: JSON.stringify({ phone }),
  });
}

export function unmuteSmsAlerts(phone: string): Promise<{ ok: true; phone: string }> {
  return authJson<{ ok: true; phone: string }>(
    `${API}/alerts/mutes/${encodeURIComponent(phone)}`,
    { method: "DELETE" },
  );
}

export function replyToInboundMessage(
  id: number,
  body: string,
  targetDeviceId?: string,
  images: File[] = [],
): Promise<{ ok: true; queueId: string }> {
  if (images.length > 0) {
    const form = new FormData();
    form.set("body", body);
    if (targetDeviceId) form.set("targetDeviceId", targetDeviceId);
    for (const image of images) form.append("images", image, image.name);
    return authForm<{ ok: true; queueId: string }>(`${API}/${id}/reply`, form);
  }
  return authJson<{ ok: true; queueId: string }>(`${API}/${id}/reply`, {
    method: "POST",
    body: JSON.stringify({ body, targetDeviceId }),
  });
}

export type InboundReplyStatus = {
  status: "queued" | "sending" | "sent" | "failed" | "canceled" | "missing";
  error: string | null;
  doneAt: string | null;
};

export function fetchReplyStatus(queueId: string): Promise<InboundReplyStatus> {
  return authJson<InboundReplyStatus>(`${API}/replies/${encodeURIComponent(queueId)}`);
}

export type SmsConversationAttachment = {
  id: number | string;
  kind: "image" | "video" | "audio" | "file";
  fileName: string;
  byteSize: number;
  contentType: string;
  status: "pending" | "ready" | "failed";
  thumbnailUrl: string | null;
  originalUrl: string | null;
};

export type SmsConversationItem = {
  id: string;
  source: "device" | "send-log" | "scheduled";
  direction: "inbound" | "outbound";
  body: string;
  at: string;
  channel: string;
  isRead: boolean | null;
  attachments?: SmsConversationAttachment[];
  deliveryStatus?:
    | "scheduled"
    | "canceled"
    | "sending"
    | "retry_wait"
    | "sent"
    | "failed"
    | "final_failed";
  scheduledAt?: string | null;
  nextAttemptAt?: string | null;
  failureReason?: string | null;
  fallbackUrl?: string | null;
  canRetry?: boolean;
  requiresRetryConfirmation?: boolean;
  attempts?: Array<{
    retryCycle: number;
    attemptNo: number;
    ok: boolean;
    error: string | null;
    at: string;
  }>;
};

export type SmsConversation = {
  phone: string;
  anchorMessageId: number | null;
  displayName: string | null;
  deviceId: string;
  sendingDevice: {
    id: string;
    phoneNumber: string | null;
    reportedPhoneNumber: string | null;
    manuallyVerifiedPhoneNumber: string | null;
    name: string;
  } | null;
  /** 번호 역조회가 아니라, 이 대화의 기존 CRM 발송 기록에 이미 연결된 고객이다. */
  customerId: number | null;
  customerName: string | null;
  threadId: string | null;
  canReply: boolean;
  replyBlockedReason: string | null;
  unreadCount: number;
  items: SmsConversationItem[];
};

export type SmsConversationRoom = {
  anchor: string;
  threadId: string | null;
  phone: string;
  deviceId: string;
  deviceLabel: string | null;
  lastBody: string;
  lastAt: string;
  lastDirection: "inbound" | "outbound";
  hasSendFailure: boolean;
  lastDeliveryStatus: string | null;
  lastAttachments: SmsConversationAttachment[];
  captureKind: "sms" | "mms" | "rcs";
};

/** 첨부 메타가 주는 고정 주소를 인증 fetch로 읽는다. private immutable 응답이라 같은 주소는
 * 브라우저 캐시가 재사용하고, 원본은 확대/재생/다운로드를 누를 때만 이 함수를 부른다. */
export async function fetchSmsAttachment(url: string): Promise<Blob> {
  const res = await authFetch(url, { cache: "force-cache" });
  if (!res.ok) throw new Error("사진을 불러오지 못했습니다");
  return res.blob();
}

/** 휴대폰 문자 앱의 실제 thread_id를 기준으로 묶은 대화방 목록. */
export function listSmsConversationRooms(): Promise<{ rooms: SmsConversationRoom[] }> {
  return authJson<{ rooms: SmsConversationRoom[] }>(`${API}/sms-conversations`);
}

export type InboundBootstrap = {
  messages: InboundList;
  devices: InboundDevice[];
  apps: InboundApp[];
  conversations: { rooms: SmsConversationRoom[] };
  mutes: { items: SmsAlertMute[] };
};

/** 첫 화면의 독립 요청들을 한 번에 받아 DB 통로 동시 점유를 막는다. */
export function fetchInboundBootstrap(): Promise<InboundBootstrap> {
  return authJson<InboundBootstrap>(`${API}/bootstrap`);
}

/** 수신 문자 한 건을 기준으로 같은 상대에게 실제 보낸 문자와 받은 답장을 시간순으로 합친다. */
export function fetchSmsConversation(
  anchor: string,
  targetDeviceId?: string | null,
): Promise<SmsConversation> {
  const query = targetDeviceId
    ? `?targetDeviceId=${encodeURIComponent(targetDeviceId)}`
    : "";
  return authJson<SmsConversation>(`${API}/conversations/${encodeURIComponent(anchor)}${query}`);
}

export function retrySmsConversationSend(
  scheduledItemId: string,
  confirmUnknownResult = false,
): Promise<{ ok: true; queueId: string }> {
  return authJson<{ ok: true; queueId: string }>(
    `${API}/conversations/${encodeURIComponent(scheduledItemId)}/retry`,
    { method: "POST", body: JSON.stringify({ confirmUnknownResult }) },
  );
}

/** 지금 고른 폰으로 같은 상대번호에서 들어온 수신 문자를 모두 읽음 처리한다. */
export function markSmsConversationRead(
  messageId: number,
  targetDeviceId?: string | null,
): Promise<{ ok: true; changed: number }> {
  const query = targetDeviceId
    ? `?targetDeviceId=${encodeURIComponent(targetDeviceId)}`
    : "";
  return authJson<{ ok: true; changed: number }>(
    `${API}/conversations/${messageId}/read${query}`,
    { method: "PATCH" },
  );
}

export function createInboundSenderPairing(
  deviceId: string,
): Promise<{ code: string; expiresAt: string }> {
  return authJson<{ code: string; expiresAt: string }>(
    `${API}/devices/${encodeURIComponent(deviceId)}/sender-pairing`,
    { method: "POST" },
  );
}

export function unlinkInboundSender(deviceId: string): Promise<{ ok: true }> {
  return authJson<{ ok: true }>(
    `${API}/devices/${encodeURIComponent(deviceId)}/sender`,
    { method: "DELETE" },
  );
}

// 앱 목록 — 앱별 필터 드롭다운 + 앱 관리(이름·무시)에 쓴다.
export function listInboundApps(): Promise<{ apps: InboundApp[] }> {
  return authJson<{ apps: InboundApp[] }>(`${API}/apps`);
}

// 앱 이름·무시 설정 — label(빈문자/null=이름 지움) 또는 ignored 를 준다.
export function patchInboundApp(
  appKey: string,
  patch: { label?: string | null; ignored?: boolean },
): Promise<InboundApp> {
  return authJson<InboundApp>(`${API}/apps/${encodeURIComponent(appKey)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
